


















import SurfaceHeader from 'vuecomponents/surface_header.vue'
import SurfacePost from 'vuecomponents/surface_post.vue'
import { Vue, Component, Watch } from 'vue-property-decorator'
import { State, Getter, namespace } from 'vuex-class'
import GridMixin from 'vuemixins/grid_mixin'
import DoubleClickToPostMixin from 'vuemixins/double_click_to_post_mixin'
import DropToPostMixin from 'vuemixins/drop_to_post_mixin'
import {
  extractPostAttributesArrayFromClipboardData,
  extractPostAttributesFromClipboardData,
  PasteType,
} from 'bits/clipboard'
import { trackEvent } from 'bits/analytics'
import window from 'bits/global'
import { isEventOnAnInputField } from 'bits/dom'
import { PostAttributes } from 'types'

const browser = namespace('browser')

@Component({
  components: {
    SurfaceHeader,
    SurfacePost,
  },
  mixins: [GridMixin, DoubleClickToPostMixin, DropToPostMixin],
})
export default class SurfaceContainerGrid extends Vue {
  @State xHeader
  @State inlinePostEdit
  @State isMobile!: boolean
  @Getter isPostable
  @Getter dir!: 'rtl' | 'ltr'
  @Getter isUsingNewComposerModal!: boolean
  @Getter isReadOnly!: boolean
  @browser.Getter windowWidth
  @namespace('post').Getter sortedPosts
  @namespace('post').Action sortedPost
  @namespace('post').Action createPost
  @namespace('draft').Action startNewDraft!: (payload: {
    attributes: PostAttributes
    shouldStartEditing: boolean
  }) => void
  @namespace('composerModal').Action('dock') dockComposerModal!: () => void
  initializeGrid!: (
    node: Node,
    savePostHandler: (data: { postCid; sectionId: never; previousCid; nextCid }) => void,
    options: Record<any, any>,
  ) => void // from GridMixin
  debouncedUpdateGrid!: () => void // from GridMixin
  enableDoubleClickToPost!: (wallContainer: Element, wishList: Element) => void // from DoubleClickToPostMixin
  disableDoubleClickToPost!: (wallContainer: Element) => void // from DoubleClickToPostMixin
  enableDropToPost!: (wallContainer: Element) => void // from DropToPostMixin
  disableDropToPost!: (wallContainer: Element) => void // from DropToPostMixin

  mounted(): void {
    this.initializeGrid(
      this.$refs.wallContainer as Node,
      (data: { postCid; sectionId: never; previousCid; nextCid }) => {
        this.sortedPost(data)
      },
      { rtl: this.dir === 'rtl' },
    )

    if (this.isPostable) {
      this.enableNewPostHandlers()
    }
  }

  beforeDestroy(): void {
    this.disableNewPostHandlers()
  }

  enableNewPostHandlers(): void {
    if (!this.isMobile) {
      this.enableDoubleClickToPost(this.$el, this.$refs.wishList as Element)
    }
    this.enableDropToPost(this.$el)
    window.addEventListener('paste', this.handleOnPaste as (e: Event) => void)
  }

  disableNewPostHandlers(): void {
    this.disableDoubleClickToPost(this.$el)
    this.disableDropToPost(this.$el)
    window.removeEventListener('paste', this.handleOnPaste as (e: Event) => void)
  }

  handleOnPaste(event: ClipboardEvent): void {
    event.stopPropagation()
    if (!this.isPostable) return
    if (isEventOnAnInputField(event)) {
      return
    }

    const data = extractPostAttributesArrayFromClipboardData(event.clipboardData)
    if (!data || !data.length) return

    // Pasting texts from some applications like Microsoft Word may also paste an image version of the text
    // we want to treat them as text
    const isRichTextPastedAsFile =
      event.clipboardData?.types?.includes('text/rtf') && event.clipboardData?.types?.includes('Files')

    // Create a post with attachment without filename
    // when PasteType is File
    const attachment = extractPostAttributesFromClipboardData(data, PasteType.File)
    if (attachment && !isRichTextPastedAsFile) {
      if (this.isUsingNewComposerModal) {
        this.startNewDraft({ attributes: attachment.postAttributes, shouldStartEditing: true })
        this.dockComposerModal()
      } else {
        this.createPost({ attributes: attachment.postAttributes })
      }
      trackEvent('Posts', 'add', `Paste ${attachment.pasteType}`)
      return
    }

    // Create a new post when PasteType is Link or Text or PostContentHash
    const dataWithoutFile = data.filter((datum) => {
      return datum?.pasteType !== PasteType.File
    })

    if (this.isUsingNewComposerModal) {
      this.startNewDraft({ attributes: dataWithoutFile[0].postAttributes, shouldStartEditing: true })
      this.dockComposerModal()
    } else {
      this.createPost({ attributes: dataWithoutFile[0].postAttributes })
    }
    trackEvent('Posts', 'add', `Paste ${dataWithoutFile[0].pasteType}`)
  }

  @Watch('isPostable')
  toggleNewPostHandlers(newValue): void {
    newValue ? this.enableNewPostHandlers() : this.disableNewPostHandlers()
  }

  @Watch('windowWidth')
  updateGridOnWindowWidthChange(): void {
    this.debouncedUpdateGrid()
  }

  get postWidth(): string {
    // returns the desired post width
    // this used to be done in scss but it caused some bugs due to roundings + we are moving away from scss
    // => do it here

    if (this.windowWidth < 481) {
      // @include respondToPhone {
      //   $margin: 8px;
      //   .wish-sizer,
      //   .wish {
      //     // we had a 0.5px so that things are rounded down, always
      //     width: calc(50% - #{3 * $margin/2} - 0.5px);
      //   }
      // }

      const wishListMargin = 8
      return `calc(50% - ${(3 * wishListMargin) / 2}px - 0.5px)`
    } else if (this.windowWidth < 1024) {
      // @include respondToPortraitTablet {
      //   $margin: 15px;
      //   .wish-sizer,
      //   .wish {
      //     width: calc(33.3% - #{4 * $margin/3} - 0.5px);
      //   }
      // }

      const wishListMargin = 15
      return `calc(33.3% - ${(4 * wishListMargin) / 3}px - 0.5px)`
    } else if (this.windowWidth === 1024) {
      // @include respondToLandscapeTablet {
      //   $margin: 16px;
      //   .wish-sizer,
      //   .wish {
      //     width: calc(25% - #{5 * $margin/4} - 0.5px);
      //   }
      // }

      const wishListMargin = 16
      return `calc(25% - ${(5 * wishListMargin) / 4}px - 0.5px)`
    } else if (this.windowWidth < 1600) {
      // $margin: 16px;
      // .wish-sizer,
      // .wish {
      //   $adjustment: 6 * $margin/5;
      //   width: calc(20% - #{$adjustment} - 0.5px);
      // }

      const wishListMargin = 16
      return `calc(20% - ${(6 * wishListMargin) / 5}px - 0.5px)`
    } else if (this.windowWidth < 1920) {
      // @media (min-width: 1600px) {
      //   $margin: 16px;
      //   .wish-sizer,
      //   .wish {
      //     width: calc(16.67% - #{7 * $margin/6} - 0.5px);
      //   }
      // }

      const wishListMargin = 16
      return `calc(16.67% - ${(7 * wishListMargin) / 6}px - 0.5px)`
    } else {
      // @include respondToHdScreen {
      //   $margin: 16px;
      //   .wish-sizer,
      //   .wish {
      //     width: calc(14.29% - #{8 * $margin/7} - 0.5px);
      //   }
      // }

      const wishListMargin = 16
      return `calc(14.29% - ${(8 * wishListMargin) / 7}px - 0.5px)`
    }
  }
}
